/**
 * OpenStreetMap helpers — no Google Maps API key required.
 */

export type LeafletMap = {
  setView: (latlng: [number, number], zoom: number) => LeafletMap;
  remove: () => void;
  fitBounds: (b: object, opts?: object) => void;
  invalidateSize?: () => void;
};

export type LeafletMarker = {
  addTo: (m: LeafletMap) => LeafletMarker;
  bindPopup: (html: string) => LeafletMarker;
  remove: () => void;
};

export type LeafletNs = {
  map: (el: HTMLElement, opts?: object) => LeafletMap;
  tileLayer: (url: string, opts?: object) => { addTo: (m: LeafletMap) => unknown };
  marker: (latlng: [number, number]) => LeafletMarker;
  circle: (latlng: [number, number], opts?: object) => LeafletMarker;
  featureGroup: (layers: LeafletMarker[]) => {
    getBounds: () => { pad: (n: number) => object };
  };
};

declare global {
  interface Window {
    L?: LeafletNs;
  }
}

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const OSM_TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

export function osmExternalUrl(lat: number, lng: number, zoom = 16): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`;
}

export function osmSearchUrl(query: string): string {
  return `https://www.openstreetmap.org/search?query=${encodeURIComponent(query)}`;
}

export function loadLeaflet(): Promise<LeafletNs> {
  if (window.L) return Promise.resolve(window.L);
  return new Promise((resolve, reject) => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existing = document.getElementById("leaflet-js") as HTMLScriptElement | null;
    const done = () => (window.L ? resolve(window.L) : reject(new Error("Leaflet failed")));
    if (existing) {
      if (window.L) done();
      else existing.addEventListener("load", done);
      return;
    }
    const script = document.createElement("script");
    script.id = "leaflet-js";
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = done;
    script.onerror = () => reject(new Error("Could not load map library"));
    document.body.appendChild(script);
  });
}

export function addOsmTiles(L: LeafletNs, map: LeafletMap): void {
  L.tileLayer(OSM_TILES, {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map);
}
